import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { usePackages } from '@/api/hooks/usePackages'
import { useSetPackageEnabled } from '@/api/hooks/useSetPackageEnabled'
import { AdminOnlyRoute } from '@/components/admin-only-route'
import { StateSwitcher } from '@/components/atoms/state-switcher'
import PageLayout from '@/components/layout/page-layout'
import { Switch } from '@/components/ui/switch'

export const Route = createFileRoute('/_authenticated/packages')({
  component: () => (
    <AdminOnlyRoute>
      <PackagesPage />
    </AdminOnlyRoute>
  ),
})

function PackagesPage() {
  const { t } = useTranslation()
  const { data: packages = [], isError, isLoading } = usePackages()
  const { mutate: setEnabled, isPending } = useSetPackageEnabled()

  const handleToggle = (packageId: string, enabled: boolean) => {
    setEnabled({ packageId, enabled }, { onError: error => toast.error(error.message) })
  }

  return (
    <PageLayout title={t('navigation.packages')}>
      <StateSwitcher
        isEmpty={packages.length === 0}
        emptyState={<p className="text-text-secondary">{t('packages.empty')}</p>}
        isError={isError}
        isLoading={isLoading}
      >
        <div className="flex flex-col gap-4">
          {packages.map(methodPackage => (
            <div key={methodPackage.id} className="card">
              <div className="flex items-start justify-between gap-4">
                <div className="flex flex-col gap-3">
                  <h3 className="heading-4">{methodPackage.title}</h3>
                  {methodPackage.description && (
                    <p className="text-text-secondary">{methodPackage.description}</p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="text-sm text-text-secondary">
                    {methodPackage.enabled ? t('packages.enabled') : t('packages.disabled')}
                  </span>
                  <Switch
                    checked={methodPackage.enabled}
                    disabled={isPending}
                    onCheckedChange={checked => handleToggle(methodPackage.id, checked)}
                  />
                </div>
              </div>

              {methodPackage.methodGroups.length > 0 && (
                <div className="flex flex-col gap-2">
                  <p className="font-medium">
                    {t('packages.categoriesInArea', {
                      count: methodPackage.methodGroups.length,
                    })}
                  </p>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                    {methodPackage.methodGroups.map(group => (
                      <div
                        key={group.id}
                        className="rounded-md bg-layer-2 p-3 flex flex-col"
                      >
                        <p className="heading-4">{group.title}</p>
                        {group.description && (
                          <p className="body-1 text-text-secondary mt-2">{group.description}</p>
                        )}
                        {group.methods.length > 0 && (
                          <div className="flex-1 flex flex-col justify-center">
                            <p className="mt-3 label-1 text-text-secondary">
                              {group.methods.join(', ')}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </StateSwitcher>
    </PageLayout>
  )
}
